import { Flex, Select, Tabs, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { APP_TIMEZONE } from '../../lib/format';
import { BikesTab } from './BikesTab';
import { LedgerTab } from './LedgerTab';
import { OperationsTab } from './OperationsTab';
import { ExceptionsTab } from './ExceptionsTab';
import { OverviewTab, type InsightsTab } from './OverviewTab';
import { PartsTab } from './PartsTab';
import { PurchasingTab } from './PurchasingTab';

import { SalesTab } from './SalesTab';

const { Title } = Typography;

/**
 * The first year of AutoCount sales the bridge sends (its `salesHistoryFrom`
 * default, 1 Jan 2023). An earlier year would only ever show an empty tab.
 */
const FIRST_YEAR = 2023;

/**
 * Insights: management figures read from AutoCount (spec §4), and the DMS's own
 * work on Operations (spec 2026-10-27). One tab per slice, after an Overview: Operations, Sales, Bikes, Parts, Receivables, Payables and Purchasing. The two ledgers ignore the year. The year belongs to the page so a later tab
 * opens on the same year.
 */
export function InsightsPage(): ReactNode {
  const { t } = useTranslation();
  // The year in Kuala Lumpur: on 1 Jan at 7am KL it is still 31 Dec in UTC.
  const thisYear = dayjs().tz(APP_TIMEZONE).year();
  const [year, setYear] = useState(thisYear);
  // ?tab=exceptions opens a tab directly, as the 08:00 digest's alert does.
  const [params, setParams] = useSearchParams();
  const asked = params.get('tab') === 'exceptions';
  const [tab, setTab] = useState<InsightsTab>(asked ? 'exceptions' : 'overview');
  // Opening the alert while already on Insights changes only the address; the
  // address follows the tab, so opening it again from another tab works too.
  useEffect(() => {
    if (asked) setTab('exceptions');
  }, [asked]);
  const choose = (next: InsightsTab): void => {
    setTab(next);
    setParams(next === 'exceptions' ? { tab: 'exceptions' } : {}, { replace: true });
  };

  const years = Array.from({ length: Math.max(1, thisYear - FIRST_YEAR + 1) }, (_, i) => thisYear - i);

  return (
    <Flex vertical gap={16}>
      <Flex align="center" justify="space-between" gap={12} wrap>
        <Title level={3} style={{ margin: 0 }}>
          {t('insights.title')}
        </Title>
        <Select<number>
          aria-label={t('insights.year')}
          value={year}
          onChange={setYear}
          style={{ width: 120 }}
          options={years.map((y) => ({ value: y, label: String(y) }))}
        />
      </Flex>
      <Tabs
        activeKey={tab}
        onChange={(key) => {
          choose(key as InsightsTab);
        }}
        items={[
          {
            key: 'overview',
            label: t('insights.tabs.overview'),
            // Unmounted when another tab is open, so its tiles never sit beside that tab's own.
            children: tab === 'overview' ? <OverviewTab year={thisYear} onOpen={choose} /> : null,
          },
          // This month, whatever the year picker says: it is about work, not history.
          {
            key: 'operations',
            label: t('insights.tabs.operations'),
            children: tab === 'operations' ? <OperationsTab /> : null,
          },
          {
            key: 'exceptions',
            label: t('insights.tabs.exceptions'),
            children: tab === 'exceptions' ? <ExceptionsTab /> : null,
          },
          { key: 'sales', label: t('insights.tabs.sales'), children: <SalesTab year={year} /> },
          { key: 'bikes', label: t('insights.tabs.bikes'), children: <BikesTab year={year} /> },
          { key: 'parts', label: t('insights.tabs.parts'), children: <PartsTab year={year} /> },
          { key: 'receivables', label: t('insights.tabs.receivables'), children: <LedgerTab ledger="AR" /> },
          { key: 'payables', label: t('insights.tabs.payables'), children: <LedgerTab ledger="AP" /> },
          { key: 'purchasing', label: t('insights.tabs.purchasing'), children: <PurchasingTab year={year} /> },
        ]}
      />
    </Flex>
  );
}
